"use client";

import React, { useState } from "react";
import { useToast } from "./Toast";

interface OSINTAnalyzerProps {
  className?: string;
}

export default function OSINTAnalyzer({ className = "" }: OSINTAnalyzerProps) {
  const [targetCompany, setTargetCompany] = useState("");
  const [industry, setIndustry] = useState("");
  const [analysisType, setAnalysisType] = useState("COMPLETA");
  const [depth, setDepth] = useState("advanced");
  const [analysis, setAnalysis] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToast();

  const runAnalysis = async () => {
    if (!targetCompany || !industry) {
      addToast("⚠️ Preencha todos os campos obrigatórios", "error");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch("/api/osint-analysis", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          targetCompany, 
          industry, 
          analysisType, 
          depth 
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setAnalysis(data);
        addToast("🔍 Análise OSINT concluída com sucesso!", "success");
      } else {
        const error = await response.json();
        addToast(error.error || "Erro na análise OSINT", "error");
      }
    } catch (error) {
      console.error("Erro:", error);
      addToast("Erro na análise OSINT", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    addToast("📋 Dados copiados!", "success");
  };

  return (
    <div className={`bg-gray-900 text-cyan-400 rounded-lg p-6 border border-cyan-500/30 font-mono ${className}`}>
      <div className="mb-6">
        <h3 className="text-2xl font-bold mb-2 text-cyan-400">
          🔍 OSINT INTELLIGENCE MODULE 🔍
        </h3>
        <div className="text-xs text-cyan-500 mb-4">
          [OSINT_FRAMEWORK] v3.1.0 | MODE: ADVANCED | AUTH: LEVEL_ADMIN
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-xs text-cyan-500 mb-2">TARGET_COMPANY:</label>
          <input
            type="text"
            value={targetCompany}
            onChange={(e) => setTargetCompany(e.target.value)}
            className="w-full bg-gray-800/50 border border-cyan-500/50 text-cyan-400 px-3 py-2 rounded focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_10px_rgba(6,182,212,0.3)]"
            placeholder="ex: Empresa Concorrente Ltda"
          />
        </div>

        <div>
          <label className="block text-xs text-cyan-500 mb-2">INDUSTRY_SECTOR:</label>
          <select
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="w-full bg-gray-800/50 border border-cyan-500/50 text-cyan-400 px-3 py-2 rounded focus:outline-none focus:border-cyan-400"
          >
            <option value="">SELECT_INDUSTRY</option>
            <option value="ecommerce">E-commerce</option>
            <option value="saas">SaaS/Software</option>
            <option value="fintech">Fintech</option>
            <option value="edtech">Edtech</option>
            <option value="healthtech">Healthtech</option>
            <option value="marketplace">Marketplace</option>
            <option value="services">Serviços</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-cyan-500 mb-2">ANALYSIS_TYPE:</label>
          <select
            value={analysisType}
            onChange={(e) => setAnalysisType(e.target.value)}
            className="w-full bg-gray-800/50 border border-cyan-500/50 text-cyan-400 px-3 py-2 rounded focus:outline-none focus:border-cyan-400"
          >
            <option value="COMPLETA">FULL_ANALYSIS</option>
            <option value="MARKETING">MARKETING_FOCUS</option>
            <option value="TECHNOLOGY">TECH_STACK_ANALYSIS</option>
            <option value="FINANCIAL">FINANCIAL_INTELLIGENCE</option>
            <option value="COMPETITIVE">COMPETITIVE_INTELLIGENCE</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-cyan-500 mb-2">DEPTH_LEVEL:</label>
          <select
            value={depth}
            onChange={(e) => setDepth(e.target.value)}
            className="w-full bg-gray-800/50 border border-cyan-500/50 text-cyan-400 px-3 py-2 rounded focus:outline-none focus:border-cyan-400"
          >
            <option value="basic">BASIC_SCAN</option>
            <option value="intermediate">INTERMEDIATE_ANALYSIS</option>
            <option value="advanced">ADVANCED_OSINT</option>
            <option value="deep">DEEP_DIVE</option>
          </select>
        </div>
      </div>

      <button
        onClick={runAnalysis}
        disabled={isLoading || !targetCompany || !industry}
        className="w-full bg-cyan-500/20 border border-cyan-500 text-cyan-400 py-3 px-6 rounded font-mono text-sm hover:bg-cyan-500/30 disabled:bg-gray-900/50 disabled:border-gray-700 disabled:text-gray-600 transition-all duration-300 mb-6"
      >
        {isLoading ? (
          <span className="flex items-center justify-center">
            <span className="animate-pulse">⚡</span> RUNNING_OSINT...
          </span>
        ) : (
          <span className="flex items-center justify-center">
            🔍 [INITIATE_SCAN] 🔍
          </span>
        )}
      </button>

      {analysis && (
        <div className="mt-6 space-y-4">
          <div className="text-xs text-cyan-500 mb-4">
            [SCAN_COMPLETE] | DATA_POINTS: {analysis.metadata?.dataPoints} | RELIABILITY: {analysis.metadata?.reliability}
          </div>
          
          {analysis.company_overview && (
            <div className="bg-gray-800/50 border border-cyan-500/30 rounded p-4 mb-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-lg font-bold text-cyan-400">COMPANY_OVERVIEW:</h4>
                <button
                  onClick={() => copyToClipboard(JSON.stringify(analysis.company_overview, null, 2))}
                  className="text-xs bg-cyan-500/20 px-2 py-1 rounded hover:bg-cyan-500/30"
                >
                  [COPY_DATA]
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="text-cyan-300">NAME: {analysis.company_overview.name}</div>
                <div className="text-cyan-300">FOUNDED: {analysis.company_overview.founded}</div>
                <div className="text-cyan-300">HEADQUARTERS: {analysis.company_overview.headquarters}</div>
                <div className="text-cyan-300">EMPLOYEES: {analysis.company_overview.employees}</div>
                <div className="text-cyan-300">WEBSITE: {analysis.company_overview.website}</div>
              </div>
              
              {analysis.company_overview.social_media && (
                <div className="mt-3">
                  <h5 className="text-sm font-bold text-cyan-400 mb-2">SOCIAL_MEDIA_FOOTPRINT:</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                    {Object.entries(analysis.company_overview.social_media).map(([platform, handle]: [string, any]) => (
                      <div key={platform} className="text-cyan-300">
                        {platform.toUpperCase()}: {handle}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {analysis.market_analysis && (
            <div className="bg-gray-800/50 border border-cyan-500/30 rounded p-4 mb-4">
              <h4 className="text-lg font-bold text-cyan-400 mb-3">MARKET_INTELLIGENCE:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="text-cyan-300">MARKET_SHARE: {analysis.market_analysis.market_share}</div>
                <div className="text-cyan-300">REVENUE_EST: {analysis.market_analysis.revenue_estimate}</div>
                <div className="text-cyan-300">GROWTH_RATE: {analysis.market_analysis.growth_rate}</div>
                <div className="text-cyan-300">CONFIDENCE: {analysis.confidence_level}</div>
              </div>
              
              {analysis.market_analysis.competitive_advantages && (
                <div className="mt-3">
                  <h5 className="text-sm font-bold text-cyan-400 mb-2">COMPETITIVE_ADVANTAGES:</h5>
                  <div className="space-y-1">
                    {analysis.market_analysis.competitive_advantages.map((advantage: string, index: number) => (
                      <div key={index} className="text-cyan-300 text-xs">• {advantage}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {analysis.technology_stack && (
            <div className="bg-gray-800/50 border border-cyan-500/30 rounded p-4 mb-4">
              <h4 className="text-lg font-bold text-cyan-400 mb-3">TECHNOLOGY_STACK:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <h5 className="text-xs font-bold text-cyan-400 mb-1">WEBSITE_TECH:</h5>
                  <div className="flex flex-wrap gap-1">
                    {analysis.technology_stack.website_tech?.map((tech: string, index: number) => (
                      <span key={index} className="bg-cyan-500/20 text-cyan-300 px-2 py-1 rounded text-xs">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h5 className="text-xs font-bold text-cyan-400 mb-1">MARKETING_TOOLS:</h5>
                  <div className="flex flex-wrap gap-1">
                    {analysis.technology_stack.marketing_tools?.map((tool: string, index: number) => (
                      <span key={index} className="bg-cyan-500/20 text-cyan-300 px-2 py-1 rounded text-xs">
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {analysis.vulnerabilities && (
            <div className="bg-red-900/20 border border-red-500/30 rounded p-4 mb-4">
              <h4 className="text-lg font-bold text-red-400 mb-3">⚠️ VULNERABILITIES_DETECTED:</h4>
              <div className="space-y-1">
                {analysis.vulnerabilities.map((vuln: string, index: number) => (
                  <div key={index} className="text-red-300 text-sm">• {vuln}</div>
                ))}
              </div>
            </div>
          )}

          {analysis.opportunities && (
            <div className="bg-green-900/20 border border-green-500/30 rounded p-4 mb-4">
              <h4 className="text-lg font-bold text-green-400 mb-3">🎯 OPPORTUNITY_TARGETS:</h4>
              <div className="space-y-1">
                {analysis.opportunities.map((opp: string, index: number) => (
                  <div key={index} className="text-green-300 text-sm">• {opp}</div>
                ))}
              </div>
            </div>
          )}

          {analysis.recommendations && (
            <div className="bg-purple-900/20 border border-purple-500/30 rounded p-4">
              <h4 className="text-lg font-bold text-purple-400 mb-3">📋 STRATEGIC_RECOMMENDATIONS:</h4>
              <div className="space-y-1">
                {analysis.recommendations.map((rec: string, index: number) => (
                  <div key={index} className="text-purple-300 text-sm">{index + 1}. {rec}</div>
                ))}
              </div>
            </div>
          )}

          <div className="text-xs text-cyan-500 mt-4">
            [ANALYSIS_COMPLETE] | TIMESTAMP: {analysis.metadata?.analyzedAt} | AUTH: ADMIN_LEVEL
          </div>
        </div>
      )}
    </div>
  );
}
